/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 18-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : By-tenant view — expandable tenant list; each row opens to its trades as cards.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Trade, Tenant, Scope } from '../types';
import { activeVersion } from '../model';
import { tenantColor, tradePalette, templateStatus, tradesForTenant, relativeWhen } from './libraryModel';
import StatusChip, { HasDraftBadge as DraftChip } from './StatusChip';
import TemplateMiniCard, { VersionPill, CountPill } from './TemplateMiniCard';

interface Props {
  trades: Trade[]; tenants: Tenant[]; filter: string;
  /** openDraft=true edits the scope's saved draft; otherwise the published current version. */
  onOpen: (scope: Scope, tenantId: string | null, tradeId: string | null, openDraft?: boolean) => void;
  /** Load the expanded tenant's current versions (lazy-on-expand) so its cards populate. */
  onSelectTenant?: (tenantId: string) => void;
}

const emptyBox = (msg: string) => (
  <Box sx={{ p: 5, textAlign: 'center', color: 'text.disabled', border: 1, borderColor: 'divider', borderRadius: 2 }}>{msg}</Box>
);

/** By-tenant view: a list of tenants, each expandable (down arrow) to reveal its trades as cards. */
export default function TenantTradeList({ trades, tenants, filter, onOpen, onSelectTenant }: Props) {
  const f = filter.trim().toLowerCase();
  const rows = useMemo(() => tenants.map(t => {
    let ts = tradesForTenant(trades, t.id);
    if (f) ts = ts.filter(x => x.name.toLowerCase().includes(f) || x.category.toLowerCase().includes(f));
    return { tenant: t, trades: ts };
  }), [f, trades, tenants]);
  const visible = f ? rows.filter(r => r.trades.length > 0) : rows;

  const [expanded, setExpanded] = useState<string | null>(tenants[0]?.id ?? null);
  useEffect(() => {
    if (visible.length && !visible.some(r => r.tenant.id === expanded)) setExpanded(visible[0].tenant.id);
  }, [f]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load the expanded tenant's versions (ref keeps a fresh loader identity from retriggering).
  const selectRef = useRef(onSelectTenant);
  useEffect(() => { selectRef.current = onSelectTenant; });
  useEffect(() => { if (expanded) selectRef.current?.(expanded); }, [expanded]);

  if (!visible.length) return emptyBox('No tenant matches your search.');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {visible.map(({ tenant, trades: ts }) => {
        const open = expanded === tenant.id;
        const tc = tenantColor(tenant.id);
        return (
          <Box key={tenant.id} sx={{ border: 1, borderColor: open ? 'primary.main' : 'divider', borderRadius: 2.5, bgcolor: 'background.paper', overflow: 'hidden', transition: 'border-color .12s' }}>
            <Box component="button" type="button" onClick={() => setExpanded(open ? null : tenant.id)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', border: 'none', cursor: 'pointer',
                fontFamily: 'inherit', px: 2, py: 1.25, textAlign: 'left',
                bgcolor: open ? (theme => alpha(theme.palette.primary.main, 0.06)) : 'transparent',
                '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) } }}>
              <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: tc, color: '#fff', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14 }}>{tenant.name[0]}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap fontWeight={700} fontSize={14}>{tenant.name}</Typography>
                <Typography fontSize={12} color="text.secondary">{ts.length} trade{ts.length !== 1 ? 's' : ''} adopted</Typography>
              </Box>
              <ExpandMoreIcon sx={{ color: 'text.secondary', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
            </Box>
            <Collapse in={open} unmountOnExit>
              <Box sx={{ p: 2, pt: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
                {ts.length === 0 ? (
                  <Typography sx={{ px: 1, py: 2, fontSize: 12.5, color: 'text.disabled' }}>This tenant hasn't adopted any trade yet.</Typography>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 1.5 }}>
                    {ts.flatMap(trade => {
                      const scope = trade.tenants[tenant.id];
                      const active = activeVersion(scope.versions);
                      const draft = scope.draft;
                      const [softBg, ink] = tradePalette(trade.id);
                      const initial = trade.name[0];
                      const whenText = (w?: string | null) => (relativeWhen(w ?? '')
                        ? <Typography component="span" sx={{ ml: 'auto', fontSize: 11, color: 'text.disabled', whiteSpace: 'nowrap', flexShrink: 0 }}>{relativeWhen(w ?? '')}</Typography>
                        : null);
                      const cards: React.ReactNode[] = [];

                      // Published template card → edits the published version.
                      if (active) {
                        cards.push(
                          <TemplateMiniCard key={`${trade.id}:pub`} initial={initial} name={trade.name} category={trade.category}
                            softBg={softBg} ink={ink} onClick={() => onOpen('tenant', tenant.id, trade.id, false)}>
                            <StatusChip status={templateStatus(active)} />
                            {active.version && <VersionPill v={active.version} />}
                            <CountPill n={(active.tasks || []).length} />
                            {whenText(active.when)}
                          </TemplateMiniCard>,
                        );
                      }

                      // Separate draft card → edits the draft.
                      if (draft) {
                        cards.push(
                          <TemplateMiniCard key={`${trade.id}:draft`} draft initial={initial} name={trade.name} category={trade.category}
                            softBg={softBg} ink={ink} onClick={() => onOpen('tenant', tenant.id, trade.id, true)}>
                            <DraftChip when={draft.when} />
                            <CountPill n={draft.taskCount} />
                            {whenText(draft.when)}
                          </TemplateMiniCard>,
                        );
                      }

                      // Neither published nor draft — a single placeholder card.
                      if (!active && !draft) {
                        cards.push(
                          <TemplateMiniCard key={`${trade.id}:empty`} initial={initial} name={trade.name} category={trade.category}
                            softBg={softBg} ink={ink} onClick={() => onOpen('tenant', tenant.id, trade.id, false)}>
                            <Typography component="span" sx={{ fontSize: 11, color: 'text.disabled' }}>{scope.loaded ? 'No published version' : 'loading…'}</Typography>
                          </TemplateMiniCard>,
                        );
                      }
                      return cards;
                    })}
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}
